/* eslint-disable */

import caches from './caches'
import auth from './auth'
import config from '../config'
import { defineAsyncComponent } from 'vue';

const router = {
	curPage: undefined,
	components: {},
	param: {},
	pathChangeListener: {},
	
	/**
	 * 注册路径变化监听器
	 * @param {string} key - 监听器唯一标识
	 * @param {Function} fun - 回调函数
	 */
	onPathChange(key, fun) {
		this.pathChangeListener[key] = fun
	},
	
	/**
	 * 移除路径变化监听器
	 * @param {string} key - 监听器唯一标识
	 */
	offPathChange(key) {
		delete this.pathChangeListener[key]
	},
	
	/**
	 * 清空当前页面参数并刷新URL
	 */
	clearParam() {
		router.param = {}
		const targetPage = this.pages[router.curPage]
		if (targetPage) {
			window.location.href = window.location.origin + '/#' + targetPage.path
			caches['cur-page-param'] = {}
		}
	},
	
	/**
	 * 路由跳转核心方法
	 * @param {string} page - 页面名称
	 * @param {Object} param - 路由参数
	 * @param {boolean} new_window - 是否新窗口打开
	 */
	to(page, param, new_window) {
		// 防止重复跳转到当前页面(无参数变化时)
		const curLocation = window.location.hash.substring(2)
		if (page === curLocation && router.curPage === curLocation && !param) {
			return
		}
		
		const toPage = this.pages[page]
		
		// 页面不存在,跳转到404
		if (!toPage) {
			this.to('404', param)
			return
		}
		
		// 权限校验: * 表示公开页面,或匹配当前用户角色
		const hasAuth = toPage.auth.indexOf('*') !== -1 || 
		                toPage.auth === config.auth || 
		                toPage.auth.indexOf(auth.user.role) !== -1
		
		if (!hasAuth) {
			this.to('405', param)
			return
		}
		
		// 构建URL参数字符串
		let paramStr = ''
		if (param && typeof param === 'object') {
			const paramPairs = Object.entries(param)
				.filter(([k, v]) => v !== undefined && v !== null)
				.map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
			
			if (paramPairs.length > 0) {
				paramStr = '?' + paramPairs.join('&')
			}
		}
		
		// 构建完整URL
		const baseUrl = location.href.substring(0, location.href.lastIndexOf('/#/'))
		const targetUrl = `${baseUrl}/#${toPage.path}${paramStr}`
		
		if (new_window) {
			window.open(targetUrl)
		} else {
			// 更新路由状态
			router.curPage = toPage.name
			router.param = param || {}
			caches['cur-page'] = toPage.name
			caches['cur-page-param'] = router.param
			
			// 触发路径变化监听器(在URL变化前触发)
			this.triggerPathChange()
			
			window.location.href = targetUrl
		}
	},
	
	/**
	 * 触发所有路径变化监听器
	 */
	triggerPathChange() {
		const funs = Object.values(this.pathChangeListener)
		for (const fun of funs) {
			try {
				fun()
			} catch (error) {
				console.error('路径变化监听器执行错误:', error)
			}
		}
	},
	
	oldLocation: undefined,
	
	/**
	 * 浏览器前进/后退事件处理
	 */
	popstate() {
		const href = window.location.href
		if (router.oldLocation && router.oldLocation !== href) {
			console.warn('router updated!', href)
			setTimeout(() => router.init(), 100)
		}
		router.oldLocation = href
	},
	
	/**
	 * 路由初始化:解析URL、恢复缓存状态、加载对应页面
	 */
	init() {
		// 重新绑定popstate事件(防止重复绑定)
		window.removeEventListener('popstate', this.popstate)
		window.addEventListener('popstate', this.popstate)
		
		// 从缓存恢复上次的页面和参数
		this.curPage = caches['cur-page']
		router.param = caches['cur-page-param'] || {}
		
		// 确保param是对象类型
		if (typeof router.param !== 'object' || router.param === null) {
			router.param = {}
		}
		
		let urlPath = ''
		
		// 如果URL中没有hash路由,跳转到默认页面
		if (location.href.indexOf('/#/') === -1) {
			if (!router.curPage || !router.pages[router.curPage]) {
				router.curPage = config.defaultPage
			}
			const defaultPagePath = router.pages[router.curPage]?.path || '/'
			location.href = `${location.origin}/#${defaultPagePath}`
			return
		}
		
		// 解析URL路径和参数
		const hashIndex = location.href.indexOf('/#/')
		const questionIndex = location.href.indexOf('?')
		
		if (questionIndex > hashIndex) {
			// 有查询参数
			urlPath = location.href.substring(hashIndex + 2, questionIndex)
			const params = location.href.split('?')[1].split('&')
			
			for (const p of params) {
				const [key, value] = p.split('=')
				if (key && value) {
					router.param[key] = decodeURIComponent(value)
				}
			}
		} else {
			// 无查询参数
			urlPath = location.href.substring(hashIndex + 2)
		}
		
		// 根据URL路径查找对应的页面
		if (!router.pages[router.curPage] || router.pages[router.curPage].path !== urlPath) {
			let foundPage = false
			for (const p of Object.values(router.pages)) {
				if (p.path === urlPath) {
					router.curPage = p.name
					foundPage = true
					break
				}
			}
			
			// 如果找不到匹配的页面,跳转到默认页面
			if (!foundPage) {
				router.curPage = config.defaultPage
			}
		}
		
		// 最终兜底:确保curPage有效
		if (!router.pages[router.curPage]) {
			router.curPage = config.defaultPage
		}
		
		// 执行路由跳转(会触发权限校验)
		router.to(router.curPage, router.param)
	}
}

// 从配置中加载页面定义
router.pages = config.pages

// 动态加载布局组件
for (const key in config.layouts) {
	router.components[key] = defineAsyncComponent(config.layouts[key])
}

// 动态加载页面组件,并初始化页面配置
for (const k of Object.keys(config.pages)) {
	const page = router.pages[k]
	page.name = k
	
	// 设置默认布局
	if (!page.layout) {
		page.layout = 'default'
	}
	
	// 设置默认权限(如果未定义)
	if (!page.auth) {
		page.auth = ['*'] // 默认公开访问
	}
	
	// 确保auth是数组格式
	if (typeof page.auth === 'string') {
		page.auth = [page.auth]
	}
	
	// 加载页面组件
	router.components['c' + page.name] = defineAsyncComponent(page.component)
}

export default router